import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { filter, Observable, map, switchMap, debounceTime, distinctUntilChanged, tap, takeUntil, Subject, of, iif, BehaviorSubject, Subscription, merge, pipe } from 'rxjs';
import { AutocompleteStrategyBase, SearchSuggestion } from './search-strategy-base'
import { OptionImageProvider } from 'src/app/shared/common-features/services/option-image-providers/option-image-provider';

@Component({
    selector: 'autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: AutocompleteComponent
        },
    ],
    standalone: false
})
export class AutocompleteComponent implements OnInit {

  @Input() inputHtmlId: string | undefined
  @Input() inputControl!: FormControl<string | SearchSuggestion | null>
  @Input() label: string = ''
  @Input() placeHolder: string = ''
  @Input() autocompleteStrategy!: AutocompleteStrategyBase
  @Input() maxSuggestions: number | undefined
  @Input() loadOnClick: boolean = false
  @Input() optionImageProvider: OptionImageProvider | undefined

  @Output() selectedSuggestion = new EventEmitter<SearchSuggestion | null>()

  suggestions$: Observable<SearchSuggestion[]> = of([])
  undoClick$ = new Subject<void>()
  isLoading$ = new BehaviorSubject<boolean>(false)

  protected readonly debounceTime: number = 500
  protected isLastEmittedValueNull: boolean = true

  constructor() {
  }

  ngOnInit(): void {
    this.initializeSuggestionsStream()
  }

  initializeSuggestionsStream() {
    this.suggestions$ = this.inputControl.valueChanges.pipe(
      debounceTime(this.debounceTime),
      filter(input => !this.isSuggestion(input)),
      distinctUntilChanged(),
      tap(() => this.isLoading$.next(true)),
      tap(() => {
        if (!this.isLastEmittedValueNull) this.selectedSuggestion.emit(null)
        this.isLastEmittedValueNull = true
      }),
      switchMap(input =>
        iif(() => !(input as string).length && !this.loadOnClick,
          of([]),
          this.autocompleteStrategy.loadSuggestions$(input as string ?? '', this.maxSuggestions).pipe(takeUntil(this.undoClick$))
        )
      ),
      tap(() => { this.isLoading$.next(false) }),
    )

  }

  onInputClick() {
    if (this.inputControl.value == '' && this.loadOnClick) this.inputControl.updateValueAndValidity()
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    this.isLastEmittedValueNull = false
    this.selectedSuggestion.emit(event.option.value)
  }

  displayFn(suggestion: SearchSuggestion | string): string {
    if (!suggestion) return ''
    return (suggestion as SearchSuggestion)['value'] ?? suggestion
  }

  onUndoClick() {
    console.log('undo')
    this.undoClick$.next(void 0)
    this.inputControl.setValue('')
    this.selectedSuggestion.emit(null)
  }

  resetInputControl() {
    this.inputControl.setValue('')
    this.isLoading$.next(false)
    this.suggestions$ = of([])
    this.initializeSuggestionsStream()
  }

  onBlur() {
    if (!this.isSuggestion(this.inputControl.value) && this.inputControl.dirty)
      this.inputControl.setValue('')
  }

  protected isSuggestion(input: string | SearchSuggestion | null): boolean {
    if (input == null) return false
    return (<SearchSuggestion>input).id !== undefined || (<SearchSuggestion>input).value !== undefined
  }

}
